import {
  Avatar,
  Breadcrumb,
  BreadcrumbProps,
  Button,
  CompactSidebarNavTrigger,
  Heading,
  Icon,
  KeyboardShortcut,
  KeyboardShortcutsHelp,
  NavLink,
  Page,
  Select,
  SidebarNav,
  SidebarNavContent,
  SidebarNavFooter,
  SidebarNavHeader,
  SidebarNavMenu,
  SidebarNavMenuButton,
  SidebarNavMenuItem,
  SidebarNavMenuSub,
  SidebarNavProvider,
  Tooltip,
  TooltipProvider,
  useIsMobile,
  useSidebarNav,
  matchesExactKey,
  createPlatformShortcut
} from '@aidkitorg/component-library';
import { Text } from '@aidkitorg/types/lib/survey';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext, UserInfoContext } from '../Context';
import { useTheme } from '../Contexts/ThemeContext';
import { useLocalizedStrings } from '../Localization';
import { LanguageDropdown } from './common/LanguageDropdown';
import { SupportedLanguage } from '@aidkitorg/i18n/lib';
import { AdminCommandPalette } from './AdminCommandPalette';
import { getNavItems } from './navigationConfig';
import { OrganizationSelector } from './components/OrganizationSelector';
import { useOrganizationContext } from '../Contexts/OrganizationContext';

export type AdminPanelPageProps = React.PropsWithChildren<{
  title: React.ReactNode | string,
  description?: React.ReactNode,
  breadcrumbs?: BreadcrumbProps['items'],
  pageActionButtons?: React.ReactElement<React.ComponentProps<typeof Button | typeof Select>>[],
  fullWidth?: boolean,
  keyboardShortcuts?: KeyboardShortcut[],
}>

const AdminPanelSidebarHeader = () => {
  const { state, isMobile, expandedTriggerRef } = useSidebarNav();
  const publicConfig = useContext(PublicConfigurationContext);
  const { logo } = useTheme();
  const L = useLocalizedStrings();
  const [showLogo, setShowLogo] = useState(state === 'expanded');
  const interfaceContext = useContext(InterfaceContext);
  const isRTL = interfaceContext.direction === 'rtl';
  const history = useHistory();

  // This is a subtle animation polish to ensure that the logo 
  // gradually appears in tandem with the sidebar nav expansion transition
  useEffect(() => {
    if (state === 'expanded') {
      // Wait for the next frame before showing the logo
      const frame = requestAnimationFrame(() => {
        setShowLogo(true);
      });
      
      return () => cancelAnimationFrame(frame);
    } else {
      // Hide the logo immediately when collapsing
      setShowLogo(false);
    }
  }, [state]);

  if (isMobile) {
    // Sheets will render a floating X close button in the top right corner
    // so the right margin is set to 9rem to account for that
    return <div className="px-[0.5rem] pb-2 mr-9 rtl:ml-9">
      <span className="text-gray-700 font-semibold text-sm">
        {publicConfig?.name || "AidKit"}
      </span>
    </div>
  }

  return (
    <div className={`flex justify-between items-start ${isMobile ? "mt-5" : ''}`}>
      <div className="w-auto rtl:sm:pl-4 sm:pr-4 max-w-48 flex-shrink-0">
        <div 
          className={`duration-200 transition-opacity ease-out ${showLogo ? 'opacity-100' : 'opacity-0'} hover:cursor-pointer focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 rounded-sm`}
          onClick={() => {
            history.push("/admin-panel/home")
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              history.push("/admin-panel/home");
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={L.admin_panel.nav.home}
        >
          {logo}
        </div>
      </div>
      <div className={state !== 'collapsed' ? 'block' : 'hidden'} >
        <Tooltip 
          text={L.component_library.sidebar_nav.collapse_sidebar} 
          side={isRTL ? "left" : "right"} 
          asChild
        >
          <div>
            <CompactSidebarNavTrigger ref={expandedTriggerRef} />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

const AdminPanelNav = () => {
  const interfaceContext = useContext(InterfaceContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const config = useContext(ConfigurationContext);
  const userInfo = useContext(UserInfoContext);
  const { path } = useRouteMatch();
  const history = useHistory();
  const L = useLocalizedStrings();
  const isRTL = interfaceContext.direction === 'rtl';
  const { state, isMobile, open } = useSidebarNav();
  const [showAvatar, setShowAvatar] = useState<boolean>(state === 'expanded');

  // Delay the avatar from appearing until the sidebar width transition is complete
  useEffect(() => {
    if (state === 'expanded') {
      const timer = setTimeout(() => {
        setShowAvatar(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Hide avatar immediately when collapsing
      setShowAvatar(false);
    }
  }, [state]);

  // Use shared navigation configuration with user permissions
  const NAV_ITEMS = getNavItems(L, userInfo.tags, config.user?.email);

  const getTranslatedText = (lang?: keyof Text, value?: string | Text) => {
    if (typeof value === 'string' || !value) {
      return value;
    }
    return value[lang ?? 'en'] ?? value['en'];
  }

  return (
    <TooltipProvider>
      <SidebarNav
        title={publicConfig?.name || "AidKit"}
        collapsible='icon'
        className="bg-white"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <SidebarNavHeader>
          <AdminPanelSidebarHeader />
        </SidebarNavHeader>

        <SidebarNavContent>
          <div className="h-full flex flex-col justify-between">
            <SidebarNavMenu>
              {NAV_ITEMS.map((item, idx) => {
                const hasItems = item.items && item.items.length > 0;
                // Find the longest matching child to handle conflicts like /settings vs /settings/manage-fields
                const matchingChildren = hasItems ? item.items?.filter(subItem => 
                  path.startsWith(subItem.href) && (path === subItem.href || path.charAt(subItem.href.length) === '/')) : [];
                const longestMatch = matchingChildren?.reduce((longest, current) => 
                  current.href.length > longest.href.length ? current : longest, matchingChildren[0]);
                const isParentActive = !!longestMatch;
                // For items with href, check if current path starts with the item's href
                // For folder items (no href), highlight if any child is active
                const isItemActive = item.href 
                  ? path.startsWith(item.href) && (path === item.href || path.charAt(item.href.length) === '/')
                  : isParentActive;

                const menuButton = (
                  <SidebarNavMenuButton 
                    href={item.href || '#'} 
                    onClick={(e) => {
                      // Allow default browser behavior for modifier keys (Cmd+click, Ctrl+click, etc.)
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                        return;
                      }
                      
                      if (item.isExternal && item.href) {
                        window.open(item.href, '_blank', 'noopener,noreferrer');
                        e.preventDefault();
                      } else if (item.href) {
                        history.push(item.href)
                        e.preventDefault()
                      } else if (hasItems && item.items) {
                        // Navigate to first child item if no href
                        history.push(item.items[0].href)
                        e.preventDefault()
                      }
                    }}
                    isActive={isItemActive}
                    className={(isParentActive ? 'bg-gray-100 text-[var(--ak-color-brand-blue-800)] ' : '') + 'cursor-pointer'}
                  >
                    {item.iconName && <Icon name={item.iconName} type="solid" role="decorative" />}
                    <span>{getTranslatedText(interfaceContext.lang, item.name)}</span>
                  </SidebarNavMenuButton>
                );

                return (
                  <SidebarNavMenuItem key={idx}>
                    {state === 'collapsed' && !isMobile ? (
                      <Tooltip 
                        text={getTranslatedText(interfaceContext.lang, item.name)} 
                        side={isRTL ? "left" : "right"} 
                        skipProvider={true}
                        asChild
                      >
                        {menuButton}
                      </Tooltip>
                    ) : (
                      menuButton
                    )}
                    {item.items && isParentActive && (
                      <SidebarNavMenuSub>
                        {item.items.map((subItem, subIdx) => {
                          const subMenuButton = (
                            <SidebarNavMenuButton 
                              variant="sub"
                              href={subItem.href} 
                              onClick={(e) => {
                                // Allow default browser behavior for modifier keys (Cmd+click, Ctrl+click, etc.)
                                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                                  return;
                                }
                                
                                history.push(subItem.href)
                                e.preventDefault()
                              }}
                              isActive={longestMatch === subItem}
                            >
                              <span>{getTranslatedText(interfaceContext.lang, subItem.name)}</span>
                            </SidebarNavMenuButton>
                          );
                          
                          return (
                            <SidebarNavMenuItem key={subIdx}>
                              {state === 'collapsed' && !isMobile ? (
                                <Tooltip 
                                  text={getTranslatedText(interfaceContext.lang, subItem.name)} 
                                  side={isRTL ? "left" : "right"} 
                                  skipProvider={true}
                                  asChild
                                >
                                  {subMenuButton}
                                </Tooltip>
                              ) : (
                                subMenuButton
                              )}
                            </SidebarNavMenuItem>
                          )
                        })}
                      </SidebarNavMenuSub>
                    )}
                  </SidebarNavMenuItem>
                )
              })}
            </SidebarNavMenu>
            {!isMobile && !open && (
              <Avatar layout="condensed" size="sm" name={`${config.user.name}`} />
            )}
          </div>
        </SidebarNavContent>

        <SidebarNavFooter>
          {config?.user && (isMobile || state === 'expanded') && (
            <div className="space-y-2">
              <Avatar
                name={`${config.user.name}`} 
                email={config.user.email}
                link={
                  publicConfig.adminPasswords && (
                    <NavLink to="/admin/account_settings">{L.admin.account_settings.title}</NavLink>
                  )
                }
                className={`transition-opacity duration-100 ease-out ${showAvatar ? 'opacity-100' : 'opacity-0'}`}
              />
              <OrganizationSelector />
            </div>
          )}
          <Button variant="secondary" onClick={(e) => { window.location.assign("/logout") }}>
            {L.menu.logout}
          </Button>
        </SidebarNavFooter>
      </SidebarNav>
    </TooltipProvider>
  )
};

export const AdminPanelPage = React.forwardRef<HTMLDivElement, AdminPanelPageProps>(
  (
    {
      title,
      description,
      breadcrumbs,
      pageActionButtons,
      fullWidth = false,
      keyboardShortcuts,
      children
    },
    ref
  ) => {
    const L = useLocalizedStrings();
    const { languages } = useTheme();
    
    // Set default keyboard shortcuts if none provided
    const defaultKeyboardShortcuts = keyboardShortcuts || [
      { key: 'B', description: "Toggle sidebar", modifier: 'meta' },
      { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
    ];

    const interfaceContext = useContext(InterfaceContext);
    const isRTL = interfaceContext.direction === 'rtl';

    // Keyboard shortcuts dialog
    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
    const toggleKeyboardHelp = useCallback(() => {
      setShowKeyboardHelp(prev => !prev);
    }, []);
    const closeKeyboardHelp = useCallback(() => {
      setShowKeyboardHelp(false);
    }, []);
    
    // Command palette
    const [showCommandPalette, setShowCommandPalette] = useState(false);
    const toggleCommandPalette = useCallback(() => {
      setShowCommandPalette(prev => !prev);
    }, []);
    const closeCommandPalette = useCallback(() => {
      setShowCommandPalette(false);
    }, []);

    // Mobile detection
    const isMobile = useIsMobile();
    
    // Platform detection for tooltip
    const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform) || 
                  /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

    // Handle keyboard shortcuts (desktop only)
    useEffect(() => {
      if (!defaultKeyboardShortcuts || isMobile) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        const isInInput = event.target instanceof HTMLInputElement || 
                         event.target instanceof HTMLTextAreaElement || 
                         event.target instanceof HTMLSelectElement;

        // Handle Cmd/Ctrl+/ key to show shortcuts
        if (matchesExactKey(event, createPlatformShortcut('/'))) {
          event.preventDefault();
          toggleKeyboardHelp();
          return;
        }

        // Handle Cmd/Ctrl+K key to show command palette (allow even when in inputs)
        if (matchesExactKey(event, createPlatformShortcut('k'))) {
          event.preventDefault();
          toggleCommandPalette();
          return;
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [defaultKeyboardShortcuts, isMobile, toggleKeyboardHelp, toggleCommandPalette]);

    // TODO: uncomment this before releasing
    // if (publicConfig && publicConfig.adminPanel === false){
    //   history.push("/");
    // }

    // Memoize PageHeader to prevent remounting when parent re-renders
    const PageHeader = React.useMemo(() => {
      const PageHeaderComponent = () => {
        const { mobileTriggerRef } = useSidebarNav();
        const isMobile = useIsMobile();

        return (
          <>
            <div className="w-full border-b bg-white">
              <div className="flex flex-col gap-y-1 py-[0.5rem] sm:gap-y-3 sm:py-[1rem]">
                <div className="w-full max-w-screen-2xl mx-auto px-[0.5rem] sm:px-[1rem]">
                  {isMobile && (
                    <div className="flex flex-col gap-y-2">
                      <div className="flex flex-row gap-x-2 pb-2 items-center">
                        <Tooltip
                          text={L.component_library.sidebar_nav.expand_sidebar}
                          side={isRTL ? "left" : "right"}
                          asChild
                        >
                          <div>
                            <CompactSidebarNavTrigger ref={mobileTriggerRef} />
                          </div>
                        </Tooltip>
                        {breadcrumbs && (
                          <div className="flex flex-wrap gap-2 items-center">
                            <Breadcrumb className="png-hide" items={breadcrumbs} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!isMobile && (
                    <div className="hidden sm:flex sm:items-center justify-between gap-x-4">
                      <div className="flex flex-row gap-x-1.5 items-center">
                        {breadcrumbs && (
                          <div className="flex gap-2 items-center">
                            <Breadcrumb className="png-hide" items={breadcrumbs} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-end max-w-screen-2xl mx-auto px-[0.5rem] sm:px-[1rem] gap-2">
                  <div className="flex flex-col gap-y-2">
                    <Heading level="h1" className="m-0">{title}</Heading>
                    {description && (
                      <div className="font-medium text-gray-600">
                        {description}
                      </div>
                    )}
                  </div>
                  {pageActionButtons && (
                    <div className="flex flex-row gap-2 flex-shrink-0">
                      {pageActionButtons.map((button, idx) => <React.Fragment key={idx}>{button}</React.Fragment>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      };

      return PageHeaderComponent;
    }, [title, description, breadcrumbs, pageActionButtons, L, isRTL]);
  
    return (
      <SidebarNavProvider>
        <Page
          ref={ref}
          className="bg-gray-50 min-w-72"
          header={<PageHeader />}
          sidebar={
            <AdminPanelNav />
          }
          footer={
            <PageFooter
              showKeyboardHelp={showKeyboardHelp}
              toggleKeyboardHelp={toggleKeyboardHelp}
              keyboardShortcuts={defaultKeyboardShortcuts}
              isMobile={isMobile}
              isMac={isMac}
              languages={languages}
            />
          }
          fullWidth={fullWidth}
        >
          {children}
        </Page>
        
        {defaultKeyboardShortcuts && (
          <KeyboardShortcutsHelp
            shortcuts={defaultKeyboardShortcuts}
            isOpen={showKeyboardHelp}
            onClose={closeKeyboardHelp}
          />
        )}
        
        <AdminCommandPalette
          isOpen={showCommandPalette}
          onClose={closeCommandPalette}
        />
      </SidebarNavProvider>
    )
  }
)

const PageFooter = ({
  showKeyboardHelp,
  toggleKeyboardHelp,
  keyboardShortcuts,
  isMobile,
  isMac,
  languages,
}: {
  showKeyboardHelp: boolean;
  toggleKeyboardHelp: () => void;
  keyboardShortcuts: KeyboardShortcut[];
  isMobile: boolean;
  isMac: boolean;
  languages: SupportedLanguage[];
}) => {
  const L = useLocalizedStrings();
  const defaultKeyboardShortcuts = keyboardShortcuts || [
    { key: 'B', description: "Toggle sidebar", modifier: 'meta' },
    { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
  ];
  
  return (
    <div className="w-full flex flex-col gap-y-2 sm:flex-row sm:justify-between sm:gap-x-2 sm:items-center">
      <div className="w-full flex flex-row space-x-5">
        <div>
          <Button variant="link" href="/privacy.html" openInNewWindow>
            {L.privacy_policy}
          </Button>
        </div>
        <div>
          <Button variant="link" href="/tos.html" openInNewWindow>
            {L.terms_and_conditions}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* Show with tooltip when the dialog is not open */}
        {defaultKeyboardShortcuts && !isMobile && !showKeyboardHelp && (
          <Tooltip text={`${L.component_library.keyboard_shortcuts_help.keyboard_shortcuts_tooltip} (${isMac ? '⌘' : 'Ctrl'}/)`} asChild>
            <Button
              variant="unstyled"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggleKeyboardHelp();
              }}
              icon={<Icon name="QuestionMarkCircleIcon" type="solid" />}
              aria-label={L.component_library.keyboard_shortcuts_help.show_keyboard_shortcuts}
              className={`p-2 hover:bg-gray-200 focus-visible:bg-gray-200 rounded-full`}
            />
          </Tooltip>
        )}
        {/* Prevent tooltip from rendering while dialog is open */}
        {defaultKeyboardShortcuts && !isMobile && showKeyboardHelp && (
          <Button
            variant="unstyled"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toggleKeyboardHelp();
            }}
            icon={<Icon name="QuestionMarkCircleIcon" type="solid" />}
            aria-label={L.component_library.keyboard_shortcuts_help.show_keyboard_shortcuts}
            className={`p-2 bg-gray-300 hover:bg-gray-300 rounded-full`}
          />
        )}
        <div className="min-w-40">
          <LanguageDropdown languages={languages} />
        </div>
      </div>
    </div>
  )
};
